import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { getUsers } from '../../store/thunks/userThunk';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../store/thunks/authThunks';
import {
    PencilIcon
  } from '@heroicons/react/outline';
import { editUser } from '../../services/user';
import swal from 'sweetalert';
import { updateUserRow } from '../../store/slices/userSlice';

const User = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, total, loading } = useSelector((state) => state.users);
  const token = useSelector((state) => state.auth.token);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [warn, setWarn] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  useEffect(() => {
    dispatch(getUsers({ page, limit: rowsPerPage, sortField, sortOrder }));
  }, [dispatch, page, rowsPerPage, sortField, sortOrder, token]);

  const columns = [
    { name: 'ID', selector: (row) => row._id, sortable: true },
    { name: 'First Name', selector: (row) => row.first_name, sortable: true },
    { name: 'Last Name', selector: (row) => row.last_name, sortable: true },
    { name: 'Gender', selector: (row) => capitalizeFirstLetter(row.gender), sortable: true },
    { name: 'Mobile', selector: (row) => row.mobile, sortable: true },
    { name: 'Email', selector: (row) => row.email, sortable: true },
    { name: 'Role', selector: (row) => capitalizeFirstLetter(row.role), sortable: true },
    { name: 'Active', selector: (row) => row.active?'Active':'Inactive', sortable: false },
    { name: 'Action', sortable: false, cell: (row) => (
      <PencilIcon
        className="w-5 h-5 text-gray-600 hover:text-blue-600 cursor-pointer"
        onClick={() => handleEdit(row)}
      />
    ) }
  ];

  const capitalizeFirstLetter = (str) => {
    if (typeof str !== 'string' || str.length === 0) {
      return str; // Handle non-string input or empty strings
    }
    const firstLetter = str.charAt(0).toUpperCase();
    const restOfString = str.slice(1);
    return firstLetter + restOfString;
  }

  const handleLogout = () => {
      dispatch(logout());
      navigate('/login');
    };

  const handleEdit = async (row) => {
    setWarn('');
    setSelectedUser(row);
    setShowEditModal(true);    
  };

  const handleChange = async (e)=>{
    setWarn('');
    setSelectedUser({ ...selectedUser, [e.target.name]: e.target.value });
  }

  const handleToggle = async (e)=>{
    setWarn('');
    setSelectedUser({ ...selectedUser, active: !selectedUser.active });
  }

  const updateUser = async ()=>{
    setWarn('');
    await editUser(selectedUser._id, {active: selectedUser.active, role: selectedUser.role}, token).then(response=>{
      setWarn('');
      setSelectedUser(null);
      setShowEditModal(false); 
      swal({
        title: "User Updated",
        text: "User updated successfully!!!",
        icon: "success",
        buttons: true,
      });
      dispatch(updateUserRow(selectedUser));
    }).catch(error=>{
      if(error.response?.data?.error.toLowerCase()==='invalid token'){
        handleLogout();
      }else{
        setWarn(error.response?.data?.error || error.message);
      }
    });
  }

  const handleAddUser = () => {
    navigate('/user/add'); // or open modal instead
  };

  return (
    <>
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">User List</h2>
          <button
            onClick={handleAddUser}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition cursor-pointer"
          >
            + Add User
          </button>
        </div>
        <DataTable
          columns={columns}
          data={list}
          progressPending={loading}
          pagination
          paginationServer
          paginationPerPage={rowsPerPage}
          paginationTotalRows={total}
          onChangePage={(page) => setPage(page)}
          onChangeRowsPerPage={(newPerPage, page) => {
            setRowsPerPage(newPerPage);
            setPage(page);
          }}
          onSort={(column, sortDirection) => {
            setSortField(column.sortField);     // or column.name if API expects field name
            setSortOrder(sortDirection);
          }}
          highlightOnHover
          pointerOnHover
          striped
        />
      </div>
      {showEditModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Edit Role</h2>
              <button
                className="text-gray-600 hover:text-gray-800 cursor-pointer"
                onClick={() => setShowEditModal(false)}
              >
                ✕
              </button>
            </div>
            {warn && (
              <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 p-4">
                <p className="text-sm text-red-700 dark:text-red-400">{warn}</p>
              </div>
            )}
            {/* Sample editable fields (you can expand this) */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                className="w-full border border-gray-300 px-3 py-2 rounded"
                value={`${selectedUser?.first_name} ${selectedUser?.last_name}` || ''}
                readOnly
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                className="w-full border border-gray-300 px-3 py-2 rounded"
                value={selectedUser?.email || ''}
                readOnly
              />
            </div>
            <div className='pb-4'>
              <label className="block mb-1">Role</label>
              <select
                name="role"
                value={selectedUser?.role}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-2 rounded"
              >
                <option value="admin">Admin</option>
                <option value="teacher">Teacher</option>
                <option value="executive">Executive</option>
              </select>
            </div>
            <div>
              <label className="block mb-1">Active: </label>
              <label className="inline-flex items-center cursor-pointer relative">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={selectedUser?.active}
                  onChange={() => handleToggle()}
                />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-green-500 transition-colors"></div>
                <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-300 peer-checked:translate-x-5"></div>
              </label>
            </div>
            <div className="text-right">
              <button
                onClick={() => setShowEditModal(false)}
                className="bg-gray-100 text-gray-800 px-4 py-2 rounded mr-2 cursor-pointer"
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 cursor-pointer"
                onClick={updateUser}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>    
  )
}

export default User
